import React, { useState, useMemo } from 'react';
import ToolShell, { ACCENT, INK, fmt, Field, input, card } from '../tools/ToolShell';

/*
 * PUBLIC, no-login lead-magnet tool: /tools/creator-gst-tax-calculator
 * Targets "do youtubers pay gst" / "creator income tax india". Creator enters
 * annual income + state-match + registration status -> sees GST obligation,
 * GST payable (CGST+SGST vs IGST), and an indicative income-tax estimate under
 * the new regime (presumptive 44ADA). Strong "consult a CA" disclaimer.
 */

const GST_THRESHOLD = 2000000;          // ₹20L services threshold (most states)
const GST_RATE = 0.18;                  // 18% on creator services (SAC 998361)

// New tax regime FY 2025-26 (AY 2026-27) slabs.
const SLABS = [
  [400000, 0.00],
  [800000, 0.05],
  [1200000, 0.10],
  [1600000, 0.15],
  [2000000, 0.20],
  [2400000, 0.25],
  [Infinity, 0.30],
];

function incomeTaxNewRegime(taxable) {
  // 87A rebate: new regime makes tax nil if total taxable income <= ₹12L.
  if (taxable <= 1200000) return 0;
  let tax = 0, prev = 0;
  for (const [cap, rate] of SLABS) {
    if (taxable > prev) { tax += (Math.min(taxable, cap) - prev) * rate; prev = cap; }
    else break;
  }
  return Math.round(tax * 1.04); // + 4% health & education cess
}

export default function GstTaxToolPage() {
  const [income, setIncome] = useState('');
  const [sameState, setSameState] = useState(true);

  const r = useMemo(() => {
    const gross = parseInt(income, 10) || 0;
    if (!gross) return null;
    const needsGst = gross > GST_THRESHOLD;
    const gst = Math.round(gross * GST_RATE);
    const taxableIncome = Math.round(gross * 0.5);  // 44ADA presumptive: 50% deemed profit
    const incomeTax = incomeTaxNewRegime(taxableIncome);
    return { gross, needsGst, gst, half: gst / 2, taxableIncome, incomeTax };
  }, [income]);

  return (
    <ToolShell
      seo={{
        title: 'Creator GST & Income Tax Calculator India 2025 — CreatorKhata',
        description: 'Do YouTubers and influencers pay GST in India? Free calculator: check if you need GST registration, how much GST to charge brands, and your estimated income tax.',
        path: '/tools/creator-gst-tax-calculator',
      }}
      h1="Creator GST & Tax Calculator (India)"
      sub="Do influencers pay GST in India? Enter your annual creator income to see your GST obligation, the GST to charge brands, and an estimated income tax. No signup."
      cta={{
        heading: 'Stop dreading tax season.',
        body: 'CreatorKhata raises GST-compliant invoices, tracks TDS deducted by brands, and keeps a clean record of every rupee — so filing your ITR takes minutes, not days.',
      }}
    >
      {/* Inputs */}
      <div style={card()}>
        <Field label="Your annual creator income (₹)">
          <input style={input} type="number" value={income} onChange={e => setIncome(e.target.value)} placeholder="e.g. 1500000" />
        </Field>
        <Field label="Is the brand in your own state?">
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[['Same state', true], ['Other state', false]].map(([label, val]) => (
              <button key={label} onClick={() => setSameState(val)} style={{
                flex: 1, padding: '0.55rem', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem',
                border: `1.5px solid ${sameState === val ? ACCENT : '#e0e0e0'}`,
                background: sameState === val ? ACCENT : 'white', color: sameState === val ? 'white' : '#666',
              }}>{label}</button>
            ))}
          </div>
        </Field>
        <p style={{ fontSize: '0.72rem', color: '#aaa', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          GST threshold for services is ₹20 lakh/year (₹10L in a few special-category states). Income tax shown uses the
          <b> new regime FY 2025-26</b> with the 44ADA presumptive scheme (50% of gross treated as profit). Indicative only.
        </p>
      </div>

      {/* Output */}
      <div style={card()}>
        {!r ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🧾</div>
            Enter your <b style={{ color: ACCENT }}>annual income</b> on the left — your GST &amp; tax position appears here <b>instantly</b>. No submit button.
          </div>
        ) : (
          <div>
            <div style={{ borderRadius: 10, padding: '0.85rem 1rem', marginBottom: '0.9rem',
              background: r.needsGst ? '#fff4e5' : '#f1f8f4',
              border: `1px solid ${r.needsGst ? '#e6a23c44' : ACCENT + '22'}`,
              color: r.needsGst ? '#8a5a00' : '#2a5d42', fontSize: '0.88rem', fontWeight: 600 }}>
              {r.needsGst
                ? '⚠️ You cross ₹20L — GST registration is mandatory. You must charge 18% GST on brand deals.'
                : '✅ Below ₹20L — GST registration is not required yet. Still raise proper invoices with your PAN.'}
            </div>

            <Row label="GST to charge brands (18%)" value={r.needsGst ? fmt(r.gst) : '—'} />
            {r.needsGst && (
              sameState
                ? <><Sub label="CGST (9%)" value={fmt(r.half)} /><Sub label="SGST (9%)" value={fmt(r.half)} /></>
                : <Sub label="IGST (18%)" value={fmt(r.gst)} />
            )}
            <Row label="Taxable income (44ADA, 50%)" value={fmt(r.taxableIncome)} />
            <Row label="Estimated income tax (new regime)" value={fmt(r.incomeTax)} accent />

            <div style={{ marginTop: '0.9rem', background: '#f1f8f4', border: `1px solid ${ACCENT}22`, borderRadius: 10, padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#2a5d42' }}>
              💡 Brands deduct <b>TDS at 10%</b> (Sec 194J) before paying you. Always show TDS as a separate line on your invoice — it's adjusted against this tax at ITR time.
            </div>

            <p style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.75rem' }}>
              Indicative estimate only — not tax advice. Actual liability depends on deductions, other income, regime choice and surcharge. Consult a CA before filing.
            </p>
          </div>
        )}
      </div>
    </ToolShell>
  );
}

function Row({ label, value, accent }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.6rem 0', borderBottom: '1px solid #eee' }}>
      <span style={{ fontSize: '0.85rem', color: '#555', fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: accent ? ACCENT : INK }}>{value}</span>
    </div>
  );
}
function Sub({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0 0.3rem 1rem' }}>
      <span style={{ fontSize: '0.78rem', color: '#999' }}>{label}</span>
      <span style={{ fontSize: '0.82rem', color: '#777', fontWeight: 600 }}>{value}</span>
    </div>
  );
}
